import React, { ReactNode, useEffect, useState } from 'react';

import styled from 'styled-components';

import { SelectAdvancedWithGroupingContext } from './SelectAdvancedWithGroupingContext';
import SelectAdvancedWithGroupingDropdown from './SelectAdvancedWithGroupingDropdown';
import Select from '../../components/lib/Select';

const StyledSelect = styled(Select)`
  && {
    height: 36px;
    .ant-select-selector {
      height: 36px;
      border-radius: 0;
      border: 1px solid var(--line-gray);
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    }
  }
`;

const Label = styled.span`
  color: var(--text-primary);
  font-size: 14px;
`;

interface Props {
  className?: string;
  disabled: boolean;
  placeholder?: string;
  options: any[];
  otherOptions?: ReactNode;
  otherOptionsLabel?: string;
  defaultValue: any[];
  onSelect: any;
  labelRender?: (args: any) => ReactNode;
  dataTestId?: string;
}

const SelectAdvancedWithGrouping = ({
  className,
  disabled,
  options,
  defaultValue,
  placeholder,
  onSelect,
  otherOptions,
  otherOptionsLabel,
  labelRender,
  dataTestId,
}: Props) => {
  const [open, setOpen] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [nonSelectedItems, setNonSelectedItems] = useState<any[]>([]);
  const [selectedGroupOptions, setSelectedGroupOptions] = useState<Set<number>>(new Set());

  const [filteredSelectedItems, setFilteredSelectedItems] = useState<any[]>([]);
  const [filteredNonSelectedItems, setFilteredNonSelectedItems] = useState<any[]>([]);

  useEffect(() => {
    const optionsToSelect = options.filter((item: any) =>
      item.options.find((option: any) => defaultValue.find((selectedValue) => selectedValue === option.id))
    );
    const optionsToUnselect = options.filter((item: any) => !optionsToSelect.find((i) => i.value === item.value));
    setSelectedItems(optionsToSelect);
    setNonSelectedItems(optionsToUnselect);
    setSelectedGroupOptions(new Set(defaultValue));
  }, [open, options, defaultValue]);

  useEffect(() => {
    const filteredSelected = selectedItems.filter((item) =>
      item.label?.toLowerCase().includes(searchKeyword?.toLowerCase())
    );
    const filteredNonSelected = nonSelectedItems.filter((item) =>
      item.label?.toLowerCase().includes(searchKeyword?.toLowerCase())
    );

    setFilteredSelectedItems(filteredSelected);
    setFilteredNonSelectedItems(filteredNonSelected);
  }, [searchKeyword, selectedItems, nonSelectedItems]);

  const addItemsToSelectedGroupOptions = (nums: number[]) => {
    setSelectedGroupOptions((prevSet) => {
      const newSet = new Set(prevSet);
      nums.forEach((num) => newSet.add(num));
      return newSet;
    });
  };

  const removeItemsFromSelectedGroupOptions = (nums: number[]) => {
    setSelectedGroupOptions((prevSet) => {
      const newSet = new Set(prevSet);
      nums.forEach((num) => newSet.delete(num));
      return newSet;
    });
  };

  const handleSelectItem = (selectedItem: any) => {
    setSelectedItems((prev) => [...prev, selectedItem]);
    setNonSelectedItems((prev) => [...prev.filter((item: any) => item.value !== selectedItem.value)]);
    addItemsToSelectedGroupOptions(selectedItem.options.map((item: any) => item.id));
  };

  const handleDeselectItem = (selectedItem: any) => {
    setSelectedItems((prev) => [...prev.filter((item: any) => item.value !== selectedItem.value)]);
    setNonSelectedItems((prev) => [...prev, selectedItem]);
    removeItemsFromSelectedGroupOptions(selectedItem.options.map((item: any) => item.id));
  };

  const handleSelectAll = () => {
    setSelectedItems([...options]);
    setNonSelectedItems([]);
    const allGroupOptionIds = options.flatMap((item) => item.options.map((option: any) => option.id));
    setSelectedGroupOptions(new Set(allGroupOptionIds));
  };

  const handleSelectNone = () => {
    setSelectedItems([]);
    setNonSelectedItems([...options]);
    setSelectedGroupOptions(new Set());
  };

  const handleSearch = (e: any) => {
    setSearchKeyword(e.target.value);
  };

  const handleApplySelection = () => {
    const optionsArray = Array.from(selectedGroupOptions);
    onSelect(optionsArray);
    setOpen(false);
  };

  let labelInfo = 'None Selected';
  const items =
    options?.filter((item: any) =>
      item.options.find((option: any) => defaultValue.find((selectedValue) => selectedValue === option.id))
    ) ?? [];

  if (items.length === 1) {
    labelInfo = items[0].label;
  } else if (items.length > 1 && items.length !== options?.length) {
    labelInfo = `${items.length} Selected`;
  } else if (items.length > 1 && items.length === options?.length) {
    labelInfo = 'All Selected';
  }
  if (otherOptionsLabel) {
    labelInfo = `${labelInfo} (${otherOptionsLabel})`;
  }

  const placeholderInfo =
    selectedItems.length > 0 || !otherOptionsLabel
      ? `${placeholder ?? 'Select'}:`
      : `${placeholder ?? 'Select'} (${otherOptionsLabel})`;

  const selectPlaceholder = labelRender ? placeholderInfo : labelInfo;

  return (
    <StyledSelect
      className={className}
      open={open}
      onDropdownVisibleChange={(visible) => setOpen(visible)}
      disabled={disabled}
      placeholder={selectPlaceholder}
      value={defaultValue}
      dataTestId={dataTestId}
      labelRender={() =>
        labelRender ? labelRender({ placeholderInfo, selectedItems, otherOptionsLabel }) : <Label>{labelInfo}</Label>
      }
      dropdownStyle={{
        padding: 0,
        borderRadius: 0,
        border: '1px solid var(--line-gray)',
        minWidth: 370,
      }}
      dropdownRender={() => (
        <SelectAdvancedWithGroupingContext.Provider
          value={{
            filteredSelectedItems,
            filteredNonSelectedItems,
            selectedGroupOptions: Array.from(selectedGroupOptions),
            addItemsToSelectedGroupOptions,
            removeItemsFromSelectedGroupOptions,
            handleSelectItem,
            handleDeselectItem,
            handleSelectAll,
            handleSelectNone,
            handleSearch,
            handleApplySelection,
          }}
        >
          <SelectAdvancedWithGroupingDropdown
            options={options}
            otherOptions={otherOptions}
            selectedItems={selectedItems}
            nonSelectedItems={nonSelectedItems}
            placeholder={placeholder}
          />
        </SelectAdvancedWithGroupingContext.Provider>
      )}
    />
  );
};

export default SelectAdvancedWithGrouping;
